import React, { useCallback, useState } from 'react';
import { Box, Icon, Portal, Text, Flex } from '@chakra-ui/react';
import { FiX } from 'react-icons/fi';
import { SimpleModalData, ModalButton } from '../../utils/modalManager';
import Button from './Button';

interface SimpleModalProps {
    modal: SimpleModalData;
    onClose: () => void;
}

const sizeMap = {
    sm: {
        minW: { base: '90vw', md: '300px' },
        maxW: { base: '90vw', md: '400px' },
    },
    md: {
        minW: { base: '90vw', md: '400px' },
        maxW: { base: '90vw', md: '500px' },
    },
    lg: {
        minW: { base: '90vw', md: '600px' },
        maxW: { base: '90vw', md: '800px' },
    },
    xl: {
        minW: { base: '90vw', md: '800px' },
        maxW: { base: '90vw', md: '1000px' },
    },
    full: {
        w: { base: '95vw', md: '80vw' },
        h: { base: '90vh', md: '95vh' },
        maxW: { base: '95vw', md: '1000px' },
    },
};

const getButtonProps = (button: ModalButton) => {
    return { buttonType: button.type };
};

const SimpleModal: React.FC<SimpleModalProps> = ({ modal, onClose }) => {
    const [asyncLoadingByIndex, setAsyncLoadingByIndex] = useState<Record<number, boolean>>({});

    const sizeStyles = sizeMap[modal.size || 'md'];
    const finalStyles = {
        ...sizeStyles,
        ...(modal.maxWidth && { maxW: modal.maxWidth }),
        ...(modal.maxHeight && { maxH: modal.maxHeight }),
    };

    // Обработчик закрытия модалки - учитывает isCloseDisabled
    const handleClose = () => {
        if (!modal.isCloseDisabled) {
            onClose();
        }
    };

    const handleButtonClick = useCallback(async (button: ModalButton, index: number) => {
        if (button.onClickAsync) {
            setAsyncLoadingByIndex((prev) => ({ ...prev, [index]: true }));
            try {
                await button.onClickAsync();
            } finally {
                setAsyncLoadingByIndex((prev) => ({ ...prev, [index]: false }));
            }
            return;
        }

        button.onClick?.();
    }, []);

    if (!modal.isOpen) return null;

    return (
        <Portal>
            {/* Backdrop */}
            <Box
                position="fixed"
                top={0}
                left={0}
                right={0}
                bottom={0}
                bg="blackAlpha.600"
                zIndex={2000}
                onClick={handleClose}
            />

            {/* Dialog Container: центрируем флексом, чтобы отступы были симметричными.
                Контейнер перекрывает весь экран, поэтому клики пропускаем на backdrop. */}
            <Box
                position="fixed"
                inset={0}
                zIndex={2001}
                display="flex"
                alignItems="center"
                justifyContent="center"
                pointerEvents="none"
            >
                {/* Dialog Content */}
                <Box
                    bg="white"
                    boxShadow="xl"
                    p={0}
                    position="relative"
                    pointerEvents="auto"
                    onClick={(e) => e.stopPropagation()}
                    maxH={{ base: '90vh', md: 'auto' }}
                    overflow={modal.size === 'full' ? 'hidden' : 'visible'}
                    {...finalStyles}
                >
                    {/* Close Button - показываем только если закрытие не отключено */}
                    {!modal.isCloseDisabled && (
                        <Button
                            position="absolute"
                            top={4}
                            right={4}
                            onClick={handleClose}
                            buttonType="ghost"
                            size="lg"
                            p={2}
                            zIndex={1}
                            minW="auto"
                            h="auto"
                        >
                            <Icon as={FiX} boxSize={6} />
                        </Button>
                    )}

                    {/* Header */}
                    {modal.title && (
                        <Box p={6} pb={4}>
                            <Text fontSize="xl" fontWeight="bold">
                                {modal.title}
                            </Text>
                        </Box>
                    )}

                    {/* Body */}
                    <Box
                        px={6}
                        pb={modal.buttons && modal.buttons.length > 0 ? 4 : 6}
                        overflow={modal.size === 'full' ? 'auto' : 'visible'}
                        maxH={modal.size === 'full' ? 'calc(100% - 140px)' : 'auto'}
                    >
                        {modal.content ? modal.content : <Text color="gray.600">{modal.message}</Text>}
                    </Box>

                    {/* Footer */}
                    {modal.buttons && modal.buttons.length > 0 && (
                        <Box p={6} pt={4}>
                            <Flex
                                gap={5}
                                justifyContent="flex-end"
                                direction={{ base: 'column-reverse', md: 'row' }}
                                w={{ base: 'full', md: 'auto' }}
                            >
                                {modal.buttons.map((button, index) => (
                                    <Button
                                        key={index}
                                        onClick={() => {
                                            void handleButtonClick(button, index);
                                        }}
                                        {...getButtonProps(button)}
                                        isLoading={button.isLoading || !!asyncLoadingByIndex[index]}
                                        disabled={button.disabled || !!asyncLoadingByIndex[index]}
                                        size="lg"
                                        fontSize="lg"
                                        w={{ base: 'full', md: 'auto' }}
                                    >
                                        {button.icon}
                                        {button.text}
                                    </Button>
                                ))}
                            </Flex>
                        </Box>
                    )}
                </Box>
            </Box>
        </Portal>
    );
};

export default SimpleModal;
